import { Box, Button, Chip, Typography } from "@mui/material";
import { AxleOrgSyncOrgFragment } from "../../../../../../types";
import { DisplayCard, DisplayCardProps } from "./DisplayCard";

export interface OrgLike<TeamType, MembershipType, AthleteType> {
  id: string | number;
  name: string;
  teams: readonly TeamType[];
  memberships: readonly MembershipType[];
  athletes: readonly AthleteType[];
}

type OrgCardProps<T> = Omit<DisplayCardProps<T>, "primaryText" | "secondaryElement" | "middleElement" | "syncDisabled"> & {
  onRetryBatch?: (element: T, batchIndex: number) => void;
  retryingBatchIndex?: number | null;
};

const formatTime = (value: string | Date | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleString();
};

const syncStatusUi = (
  org: AxleOrgSyncOrgFragment
): { chipLabel: string; color: "default" | "info" | "success" | "error"; detail: string | null } | null => {
  const job = org.latestAxleOrgSyncJob;
  if (!job) {
    return null;
  }

  const started = formatTime(job.startedAt);
  const finished = formatTime(job.finishedAt);
  const done = job.completedBatchCount + job.failedBatchCount;

  switch (job.status) {
    case "pending":
    case "inProgress": {
      const progress = job.batchCount > 0 ? `${done}/${job.batchCount} batches` : "starting…";
      return {
        chipLabel: `In progress · ${progress}`,
        color: "info",
        detail: [started ? `Started ${started}` : null, job.failedBatchCount > 0 ? job.errorMessage : null].filter(Boolean).join(" · "),
      };
    }
    case "completed":
      return {
        chipLabel: job.batchCount > 0 ? `Completed · ${job.batchCount} batches` : "Completed",
        color: "success",
        detail: [started ? `Started ${started}` : null, finished ? `Finished ${finished}` : null].filter(Boolean).join(" · "),
      };
    case "failed":
      return {
        chipLabel: job.batchCount > 0 ? `Failed · ${done}/${job.batchCount}` : "Failed",
        color: "error",
        detail: [started ? `Started ${started}` : null, finished ? `Finished ${finished}` : null, job.errorMessage]
          .filter(Boolean)
          .join(" · "),
      };
    default:
      return null;
  }
};

const isSyncInFlight = (org: AxleOrgSyncOrgFragment): boolean => {
  const status = org.latestAxleOrgSyncJob?.status;
  return status === "pending" || status === "inProgress";
};

export const OrgCard = <TeamType, MembershipType, AthleteType, T extends OrgLike<TeamType, MembershipType, AthleteType>>(
  props: OrgCardProps<T>
) => {
  const { element, onRetryBatch, retryingBatchIndex = null, ...displayProps } = props;
  const primaryText = element.name;
  const orgCompositionText = `${element.teams.length} teams, ${element.athletes.length} athletes, ${element.memberships.length} active memberships`;

  const axleOrg = element as unknown as AxleOrgSyncOrgFragment;
  const status = syncStatusUi(axleOrg);
  const failedBatchIndexes = props.mode === "Sync" ? axleOrg.latestAxleOrgSyncJob?.failedBatchIndexes ?? [] : [];
  const middleElement = status ? (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5, mr: 1 }}>
      <Chip size="small" label={status.chipLabel} color={status.color} variant="outlined" />
      {status.detail && (
        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 280, textAlign: "right" }}>
          {status.detail}
        </Typography>
      )}
      {failedBatchIndexes.map(batchIndex => (
        <Button
          key={batchIndex}
          size="small"
          variant="outlined"
          disabled={!onRetryBatch || retryingBatchIndex !== null}
          onClick={event => {
            event.stopPropagation();
            onRetryBatch?.(element, batchIndex);
          }}
        >
          {retryingBatchIndex === batchIndex ? "Retrying…" : `Retry batch ${batchIndex}`}
        </Button>
      ))}
    </Box>
  ) : null;

  return (
    <DisplayCard
      {...displayProps}
      element={element}
      primaryText={primaryText}
      secondaryElement={orgCompositionText}
      middleElement={middleElement}
      syncDisabled={isSyncInFlight(axleOrg)}
    />
  );
};
